// src/hooks/useInstructorLessons.js
//
// Pass 67 (+ Pass 84.2) — the instructor's own lessonInstances, live, indexed by bookingId. Lets My Schedule show the
// correct Pass-66 delivery action per booking (Start / Open / View) without any read-on-render write.
//
// Pass 84.2 — the query is scoped to the CURRENT canonical BUSINESS instructor id (`instructorId`) + organizationId,
// NOT the stored `instructorAuthUserId` snapshot. An account relink updates the canonical membership (and the booking
// backfill), so the new Auth account — resolving to the SAME business member id — keeps seeing its existing lessons
// with no lesson-identity migration. The business member id is resolved by the caller from the selected org's canonical
// membership (never inferred from name/email). Firestore Rules authorize this read via the current member link
// (canReadLesson → lessonInstructorIsCurrentMember).

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeLessonInstanceRecord } from '../utils/lessonInstancePersistence';

export function useInstructorLessons(authUid, organizationId = '', instructorMemberUserId = '') {
  // Pass 83B.2 §16 / 84.2 — scope-keyed snapshot { scopeKey, items }. The lesson map is exposed ONLY when the snapshot
  // was produced for the CURRENTLY requested Auth uid + org + canonical member scope, so a prior org's / prior account's
  // lessons are suppressed SYNCHRONOUSLY at render (before the re-subscribe effect runs), never merely cleared after.
  const [snap, setSnap] = useState({ scopeKey: '', items: [] });
  const requestedScopeKey = authUid && organizationId && instructorMemberUserId
    ? `${authUid}::${organizationId}::${instructorMemberUserId}` : '';
  useEffect(() => {
    setSnap({ scopeKey: requestedScopeKey, items: [] });
    // FAIL CLOSED: require an Auth identity, a selected organization, AND a resolved canonical member id. A missing
    // canonical member (e.g. not yet linked) runs NO query — never an identity-only or cross-org read.
    if (!authUid || !organizationId || !instructorMemberUserId) { return undefined; }
    // Two equality filters (instructorId + organizationId) served by single-field indexes (no composite index).
    // lessonInstances always carry organizationId + the business instructorId (getOrCreateLessonInstanceForBooking).
    const q = query(collection(db, 'lessonInstances'), where('instructorId', '==', instructorMemberUserId), where('organizationId', '==', organizationId));
    const unsub = onSnapshot(
      q,
      (s) => setSnap({ scopeKey: requestedScopeKey, items: s.docs.map((d) => normalizeLessonInstanceRecord(d, d.id)) }),
      (err) => console.error('Instructor lessons listener error:', err),
    );
    return () => unsub();
  }, [authUid, organizationId, instructorMemberUserId, requestedScopeKey]);
  // booking-linked lessons only → Map<bookingId, lesson>. Empty unless the snapshot matches the current scope.
  return useMemo(() => {
    const m = new Map();
    if (!requestedScopeKey || snap.scopeKey !== requestedScopeKey) return m;
    for (const l of snap.items) if (l.bookingId) m.set(l.bookingId, l);
    return m;
  }, [snap, requestedScopeKey]);
}

export default useInstructorLessons;
